import React, { useState, useEffect, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import { supabase } from '../../utils/supabase';
import { Card } from '../../components/common/Card';
import { Heading, Text } from '../../components/common/Typography';
import { BackButton } from '../../components/common';
import GameHistoryTable from '../../components/player/GameHistoryTable';
import { fetchPlayerUnifiedGameHistory } from '../../services/playerService';

/**
 * ProfileMyGames - Page showing all games across all clubs for the current user
 * Displays KPI boxes at the top and a comprehensive game history table
 */
const ProfileMyGames = () => {
  const { currentUser } = useOutletContext();
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalGames, setTotalGames] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [kpis, setKpis] = useState({
    totalGames: 0,
    wins: 0,
    losses: 0,
    winRate: 0,
    totalSessions: 0
  });
  const pageSize = 50; // Load 50 games at a time

  // Fetch KPI stats and total count (single efficient query)
  useEffect(() => {
    const loadKPIStats = async () => {
      if (!currentUser?.id) return;

      try {
        // Use RPC function for efficient server-side aggregation
        // If RPC doesn't exist, fall back to fetching minimal data
        const { data: rpcData, error: rpcError } = await supabase.rpc(
          'get_player_game_kpis',
          { p_player_id: currentUser.id }
        ).single();

        if (!rpcError && rpcData) {
          // RPC function exists and returned data
          setKpis({
            totalGames: rpcData.total_games || 0,
            wins: rpcData.wins || 0,
            losses: rpcData.losses || 0,
            winRate: rpcData.win_rate || 0,
            totalSessions: rpcData.total_sessions || 0
          });
          setTotalGames(rpcData.total_games || 0);
          return;
        }

        // Fallback: Fetch minimal data and calculate client-side
        // Only fetch result and session_id (2 fields vs full game objects)
        const { data, error: statsError } = await supabase
          .from('player_game_history')
          .select('result, session_id')
          .eq('player_id', currentUser.id);

        if (statsError) {
          console.error('Error loading KPI stats:', statsError);
          return;
        }

        if (!data || data.length === 0) {
          setKpis({
            totalGames: 0,
            wins: 0,
            losses: 0,
            winRate: 0,
            totalSessions: 0
          });
          setTotalGames(0);
          return;
        }

        // Calculate KPIs from aggregated data
        const totalGamesCount = data.length;
        const wins = data.filter(g => g.result === 'win').length;
        const losses = data.filter(g => g.result === 'loss').length;
        const winRate = totalGamesCount > 0 ? Math.round((wins / totalGamesCount) * 100) : 0;
        
        // Count unique sessions
        const uniqueSessions = new Set(
          data.map(g => g.session_id).filter(id => id !== null && id !== undefined)
        );
        const totalSessions = uniqueSessions.size;

        setKpis({
          totalGames: totalGamesCount,
          wins,
          losses,
          winRate,
          totalSessions
        });
        setTotalGames(totalGamesCount);
      } catch (err) {
        console.error('Error loading KPI stats:', err);
      }
    };

    loadKPIStats();
  }, [currentUser?.id]);

  // Fetch games for the player (across all clubs) with pagination
  useEffect(() => {
    const loadGames = async () => {
      if (!currentUser?.id) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const offset = (currentPage - 1) * pageSize;
        const pageGames = await fetchPlayerUnifiedGameHistory(
          currentUser.id,
          null, // No club filter - get all clubs
          null, // No season filter
          pageSize, // Load 50 at a time
          offset
        );

        if (currentPage === 1) {
          // First page - replace games
          setGames(pageGames || []);
        } else {
          // Subsequent pages - append games
          setGames(prev => [...prev, ...(pageGames || [])]);
        }
      } catch (err) {
        console.error('Error loading games:', err);
        setError('Failed to load games. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadGames();
  }, [currentUser?.id, currentPage]);

  // KPIs are now loaded from database aggregation (see useEffect above)

  if (loading) {
    return (
      <div className="space-y-6">
        <BackButton onClick={() => window.history.back()}>
          Back
        </BackButton>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <BackButton onClick={() => window.history.back()}>
          Back
        </BackButton>
        <Card variant="default" size="large">
          <div className="text-center py-8">
            <Text className="text-red-600 mb-4">{error}</Text>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark"
            >
              Retry
            </button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <BackButton onClick={() => window.history.back()}>
        Back
      </BackButton>

      {/* Header */}
      <div>
        <Heading level={2} className="mb-2">
          My Games
        </Heading>
        <Text className="text-slate-600">
          View all your games across all clubs with detailed statistics and performance metrics
        </Text>
      </div>

      {/* KPI Boxes */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Total Games */}
        <Card variant="default" size="medium">
          <div className="text-center">
            <Text className="text-sm text-slate-600 mb-2">Total Games</Text>
            <Heading level={3} className="text-3xl font-bold text-primary">
              {kpis.totalGames}
            </Heading>
          </div>
        </Card>

        {/* Win Rate */}
        <Card variant="default" size="medium">
          <div className="text-center">
            <Text className="text-sm text-slate-600 mb-2">Win Rate</Text>
            <Heading level={3} className="text-3xl font-bold text-primary">
              {kpis.winRate}%
            </Heading>
            <Text className="text-xs text-slate-500 mt-1">
              {kpis.wins}W / {kpis.losses}L
            </Text>
          </div>
        </Card>

        {/* Total Sessions */}
        <Card variant="default" size="medium">
          <div className="text-center">
            <Text className="text-sm text-slate-600 mb-2">Total Sessions</Text>
            <Heading level={3} className="text-3xl font-bold text-primary">
              {kpis.totalSessions}
            </Heading>
          </div>
        </Card>
      </div>

      {/* Game History Table */}
      <div className="mt-6">
        {games.length === 0 && !loading ? (
          <Card variant="default" size="large">
            <div className="text-center py-12">
              <div className="text-6xl mb-4">🎮</div>
              <Heading level={3} className="mb-2">No Games Yet</Heading>
              <Text className="text-slate-600">
                Your game history will appear here once you start playing matches.
              </Text>
            </div>
          </Card>
        ) : (
          <>
            <GameHistoryTable 
              games={games} 
              pageSize={20}
              hideDateColumn
              clubId={null}
            />
            {/* Load More Button */}
            {games.length < totalGames && (
              <div className="mt-4 text-center">
                <button
                  onClick={() => setCurrentPage(prev => prev + 1)}
                  disabled={loading}
                  className="px-6 py-2 bg-primary text-white rounded-md hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Loading...' : `Load More (${games.length} of ${totalGames} games)`}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ProfileMyGames;

